//ionic
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonToolbar,
  useIonRouter,
  IonIcon,
  IonButton,
} from "@ionic/react";

//icons
import { arrowBackOutline } from "ionicons/icons";

import "./mainExamenes.css";
import { useParams } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { tokenState } from "recoil/tokenAtom";
import { examAbandonAtom } from "recoil/examAbandonAtom";
import { userInfoState } from "recoil/User";
import { CertificadoExamenModal } from "components/modal/certificado-examen/CertificadoExamenModal";
import { AbandonExamenModal } from "components/modal/abandon-examen/AbandonExamenModal";
import { useGetModuleByID } from "hooks/aprendizaje/useGetModuleByID";
import { useGetExamQuestions } from "hooks/examen/useGetExamQuestions";
import { useEffect, useState, useCallback, useRef } from "react";
import ExamQuestions, {
  type IExamQuestionsHandle,
  type IAbandonDataRef,
} from "./ExamQuestions";
import type { IExamAnswerResult } from "services/interfaces/IExam";
import { postExamAnswersOnUnload } from "services/sessions";

interface IParamsExamenes {
  idBasis: string;
  idModule: string;
  idExam: string;
}

export interface IGridStyles {
  [category: string]: { marginTop: string };
}

const DEFAULT_APPROVAL_PERCENTAGE = 70;

const ABANDON_MESSAGE =
  "¿Está seguro que desea abandonar? Se computará el examen como realizado y calculará la nota sobre lo respondido.";

export const MainExamenes: React.FC = () => {
  const navigation = useIonRouter();
  const token = useRecoilValue(tokenState);
  const userInfo = useRecoilValue(userInfoState);

  const { idBasis, idModule, idExam }: IParamsExamenes =
    useParams<IParamsExamenes>();
  const [examStarted, setExamStarted] = useState<boolean>(false);
  const [examResult, setExamResult] = useState<IExamAnswerResult | null>(null);
  const [showCertificadoModal, setShowCertificadoModal] = useState(false);
  const [showAbandonModal, setShowAbandonModal] = useState(false);

  const examQuestionsRef = useRef<IExamQuestionsHandle>(null);
  const abandonDataRef = useRef<IAbandonDataRef | null>(null);
  const setExamAbandon = useSetRecoilState(examAbandonAtom);

  const { ModuleById, handleGetModuleById } = useGetModuleByID();
  const { examQuestions, handleGetExamQuestions } = useGetExamQuestions();

  const name = ModuleById && ModuleById.name;
  const userId = token?.userId ?? 0;

  useEffect(() => {
    handleGetModuleById(Number(idBasis), Number(idModule));
    handleGetExamQuestions(Number(idBasis), Number(idModule), Number(idExam));
  }, [handleGetModuleById, handleGetExamQuestions, idBasis, idModule, idExam]);

  const handleStartExam = () => {
    setExamResult(null);
    setExamStarted(true);
  };

  const handleExamFinish = useCallback((result: IExamAnswerResult) => {
    setExamResult(result);
    setExamStarted(false);
  }, []);

  useEffect(() => {
    setExamAbandon((prev) => ({ ...prev, inProgress: examStarted }));
    return () => {
      setExamAbandon((prev) => ({ ...prev, inProgress: false }));
    };
  }, [examStarted, setExamAbandon]);

  const examAbandonState = useRecoilValue(examAbandonAtom);

  useEffect(() => {
    if (examAbandonState.requestedNavigation) {
      setShowAbandonModal(true);
    }
  }, [examAbandonState.requestedNavigation]);

  const handleBackClick = useCallback(() => {
    if (!examStarted) {
      navigation.push("/tabs/home", "forward", "pop");
      return;
    }
    setShowAbandonModal(true);
  }, [examStarted]);

  const handleAbandonConfirm = useCallback(async () => {
    const targetPath = examAbandonState.requestedNavigation ?? "/tabs/home";
    const success = await examQuestionsRef.current?.submitAndFinish();
    setShowAbandonModal(false);
    setExamAbandon((prev) => ({ ...prev, requestedNavigation: null }));
    if (success) {
      navigation.push(targetPath, "forward", "pop");
    }
  }, [navigation, examAbandonState.requestedNavigation, setExamAbandon]);

  const handleAbandonCancel = useCallback(() => {
    setShowAbandonModal(false);
    setExamAbandon((prev) => ({ ...prev, requestedNavigation: null }));
  }, [setExamAbandon]);

  useEffect(() => {
    if (!examStarted) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ABANDON_MESSAGE;
      return ABANDON_MESSAGE;
    };
    const handlePageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return;
      const data = abandonDataRef.current;
      if (data?.selectedAnswers && data.questions?.length) {
        const answers = data.questions
          .filter((q) => data.selectedAnswers[q.idExamQuestions] != null)
          .map((q) => ({
            idExamQuestions: q.idExamQuestions,
            idExamQuestionsAnswers: data.selectedAnswers[q.idExamQuestions],
          }));
        if (answers.length > 0) {
          postExamAnswersOnUnload(idBasis, idModule, idExam, {
            userId: data.userId,
            answers,
          });
        }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [examStarted, idBasis, idModule, idExam, userId]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <article style={{ display: "flex", width: 300 }}>
            <IonButtons slot="start" onClick={handleBackClick}>
              <IonIcon
                style={{ width: 24, height: 24, marginLeft: 10 }}
                icon={arrowBackOutline}
              />
            </IonButtons>
            <p style={{ fontSize: 18, marginLeft: 10, fontWeight: 700 }}>
              {name}
            </p>
          </article>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {!examStarted && examResult == null && (
          <>
            <p className="main-examenes-title">{name}</p>
            {examQuestions && (
              <>
                <ul className="main-examenes-list">
                  <li>
                    Duración del examen:{" "}
                    {examQuestions.examDuration != null
                      ? `${examQuestions.examDuration} minutos`
                      : "—"}
                  </li>
                  <li>
                    Cantidad de preguntas:{" "}
                    {examQuestions.questions?.length ?? 0}
                  </li>
                  <li>
                    Resultado necesario para aprobar:{" "}
                    {examQuestions.approvalPercentage ??
                      DEFAULT_APPROVAL_PERCENTAGE}
                    %
                  </li>
                </ul>
                <section className="main-examenes-info">
                  <h3 className="main-examenes-info-title">Importante</h3>
                  <p className="main-examenes-info-text">
                    No se puede volver a preguntas anteriores ni marcar para
                    revisar. El avance es secuencial; esto simula una toma de
                    decisiones como en situación real de juego.
                  </p>
                  <p className="main-examenes-info-text">
                    En caso de reprobar el examen, quedará momentáneamente
                    inhabilitado volver a rendirlo durante un plazo de 15 días.
                    Este plazo promueve la revisión del contenido antes de
                    volver al examen.
                  </p>
                </section>
                <IonButton
                  className="main-examenes-start-btn"
                  onClick={handleStartExam}
                >
                  Comenzar Examen
                </IonButton>
              </>
            )}
          </>
        )}
        {examStarted && examQuestions && userId ? (
          <ExamQuestions
            ref={examQuestionsRef}
            examQuestions={examQuestions}
            idBasis={idBasis}
            idModule={idModule}
            idExam={idExam}
            userId={userId}
            onFinish={handleExamFinish}
            abandonDataRef={abandonDataRef}
          />
        ) : null}

        {examResult != null && !examStarted && (
          <div className="main-examenes-result">
            <h2 className="main-examenes-result-title">Resultado del examen</h2>
            <div className="main-examenes-result-card">
              <p className="main-examenes-result-score">
                Puntuación: {examResult.result}%
              </p>
              <p className="main-examenes-result-detail">
                Respuestas correctas: {examResult.correctAnswers} de{" "}
                {examResult.totalQuestions}
              </p>
              <p
                className={
                  examResult.result >=
                  (examQuestions?.approvalPercentage ??
                    DEFAULT_APPROVAL_PERCENTAGE)
                    ? "main-examenes-result-status main-examenes-result-approved"
                    : "main-examenes-result-status main-examenes-result-failed"
                }
              >
                {examResult.result >=
                (examQuestions?.approvalPercentage ??
                  DEFAULT_APPROVAL_PERCENTAGE)
                  ? "Aprobado"
                  : "No aprobado"}
              </p>
            </div>
            {examResult.result >=
            (examQuestions?.approvalPercentage ??
              DEFAULT_APPROVAL_PERCENTAGE) ? (
              <IonButton
                className="main-examenes-progress-btn"
                onClick={() => setShowCertificadoModal(true)}
              >
                Ver certificado
              </IonButton>
            ) : (
              <section className="main-examenes-info main-examenes-not-certified">
                <h3 className="main-examenes-info-title">
                  Módulo no certificado
                </h3>
                <p className="main-examenes-info-text">
                  En esta evaluación no alcanzaste el estándar mínimo requerido
                  para certificar el módulo.
                </p>
                <p className="main-examenes-info-text">
                  El resultado indica que algunos conceptos clave aún no están
                  consolidados al nivel exigido para la certificación
                  respectiva.
                </p>
                <p className="main-examenes-info-text">
                  Te recomendamos reveer nuevamente el contenido del módulo
                  antes de realizar un nuevo intento. Podrá volver a rendir el
                  examen luego de los próximos 15 días.
                </p>
              </section>
            )}
          </div>
        )}
        {showCertificadoModal &&
          examResult &&
          examResult.result >=
            (examQuestions?.approvalPercentage ??
              DEFAULT_APPROVAL_PERCENTAGE) && (
            <CertificadoExamenModal
              isOpen={showCertificadoModal}
              handleClose={() => setShowCertificadoModal(false)}
              sessionName={name ?? ""}
              userName={
                `${userInfo.firstName ?? ""} ${
                  userInfo.lastName ?? ""
                }`.trim() || "Usuario"
              }
              result={examResult.result}
              idBasis={idBasis}
              idModule={idModule}
              onGoToProgress={() => {
                setShowCertificadoModal(false);
                navigation.push("/tabs/progress", "forward", "pop");
              }}
            />
          )}
        {showAbandonModal && (
          <AbandonExamenModal
            isOpen={showAbandonModal}
            onConfirm={handleAbandonConfirm}
            onCancel={handleAbandonCancel}
          />
        )}
      </IonContent>
    </IonPage>
  );
};
